import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getToken } from '../../services/api';

const ContentSummarize = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('text');

  // API Key selection
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState(null);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  // Input states
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Result states
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // History
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch API keys and history on mount
  useEffect(() => {
    fetchApiKeys();
    fetchHistory();
  }, []);

  const [apiKeyError, setApiKeyError] = useState(null);

  const fetchApiKeys = async () => {
    setIsLoadingKeys(true);
    setApiKeyError(null);
    try {
      const token = getToken();

      // 토큰이 없으면 로그인 필요
      if (!token) {
        setApiKeyError('로그인이 필요합니다.');
        setApiKeys([]);
        return;
      }

      const response = await fetch('https://api.ilouli.com/api/admin/api-keys/available', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.keys) {
          setApiKeys(data.keys);
          setApiKeyError(null);

          // Auto-select Gemini 2.5 Flash if available, otherwise first key
          const geminiKey = data.keys.find(
            key => key.provider === 'gemini' && key.model === 'gemini-2.5-flash'
          );
          const defaultKey = geminiKey || data.keys[0];

          if (defaultKey) {
            setSelectedApiKeyId(defaultKey.id);
          }
        }
      } else {
        // HTTP 에러 처리
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          setApiKeyError('인증이 만료되었습니다. 다시 로그인해주세요.');
        } else if (response.status === 403) {
          setApiKeyError('접근 권한이 없습니다.');
        } else {
          setApiKeyError(errorData.error || 'API 키 목록을 불러오는데 실패했습니다.');
        }
        setApiKeys([]);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
      setApiKeyError('네트워크 오류: API 키 목록을 불러오는데 실패했습니다.');
      setApiKeys([]);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const token = getToken();
      const response = await fetch('https://api.ilouli.com/api/ai/summarize/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHistory(data.data || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const allowedExtensions = ['.pdf', '.docx', '.txt'];
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExt)) {
      setError('지원하지 않는 파일 형식입니다. PDF, DOCX, TXT 파일만 업로드 가능합니다.');
      setFile(null);
      return;
    }

    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('파일 크기가 너무 큽니다. 최대 10MB까지 업로드 가능합니다.');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      handleFileChange({ target: { files: [droppedFile] } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate API key selection
    if (!selectedApiKeyId) {
      setError('AI 모델을 선택해주세요.');
      return;
    }

    // Validate input based on active tab
    if (activeTab === 'text' && !textInput.trim()) {
      setError('텍스트를 입력해주세요.');
      return;
    }
    if (activeTab === 'url' && !urlInput.trim()) {
      setError('URL을 입력해주세요.');
      return;
    }
    if (activeTab === 'file' && !file) {
      setError('파일을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = getToken();
      let response;

      if (activeTab === 'file') {
        // File upload with FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('inputType', 'file');
        formData.append('apiKeyId', selectedApiKeyId);

        response = await fetch('https://api.ilouli.com/api/ai/summarize', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Text or URL
        response = await fetch('https://api.ilouli.com/api/ai/summarize', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            inputType: activeTab,
            text: activeTab === 'text' ? textInput : undefined,
            url: activeTab === 'url' ? urlInput : undefined,
            apiKeyId: selectedApiKeyId
          })
        });
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setResult(data.data);
        fetchHistory(); // Refresh history
        // Clear inputs
        setTextInput('');
        setUrlInput('');
        setFile(null);
      } else {
        setError(data.error || '요약 생성에 실패했습니다.');
      }
    } catch (err) {
      console.error('Summarization error:', err);
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryItemClick = (item) => {
    // Format history item to match result structure
    setResult({
      summary: item.summary,
      keywords: item.keywords,
      keyPoints: item.keyPoints
    });
    setSelectedItem(item);
    // Scroll to results
    setTimeout(() => {
      document.querySelector('.results-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleDeleteItem = async (e, itemId) => {
    e.stopPropagation();
    if (!window.confirm('이 요약 결과를 삭제하시겠습니까?')) return;

    try {
      const token = getToken();
      const response = await fetch(`https://api.ilouli.com/api/ai/summarize/history/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setHistory(prev => prev.filter(item => item.id !== itemId));
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(null);
          setResult(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleClear = () => {
    setTextInput('');
    setUrlInput('');
    setFile(null);
    setError(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const renderResult = () => {
    if (!result) return null;

    return (
      <>
        {/* Keywords */}
        {result.keywords && result.keywords.length > 0 && (
          <section className="mb-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
              <span className="text-xl">🏷️</span>
              {t('contentSummarize.result.keywords')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {result.keywords.map((keyword, index) => (
                <span key={index} className="inline-block px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-full whitespace-nowrap">{keyword}</span>
              ))}
            </div>
          </section>
        )}

        {/* Summary */}
        {result.summary && (
          <section className="mb-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
              <span className="text-xl">📝</span>
              요약
            </h3>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-base text-gray-900 leading-relaxed m-0">{result.summary}</p>
            </div>
          </section>
        )}

        {/* Organized (if available) */}
        {result.organized && (
          <section className="mb-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
              <span className="text-xl">📋</span>
              정리
            </h3>
            <div className="p-4 bg-gray-50 rounded-lg">
              {result.organized.sections?.map((section, idx) => (
                <div key={idx} className="mb-6 last:mb-0">
                  <h4 className="text-base font-semibold text-gray-900 mb-4 pl-2 border-l-[3px] border-blue-500">{section.heading}</h4>
                  {section.content && section.content.length > 0 && (
                    <ul className="m-0 mb-4 pl-8 list-disc">
                      {section.content.map((item, i) => (
                        <li key={i} className="text-base text-gray-900 leading-relaxed mb-2">{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.quotes && section.quotes.length > 0 && (
                    <blockquote className="my-4 py-4 px-6 bg-white border-l-4 border-blue-500 rounded-lg text-base text-gray-600 italic leading-relaxed">
                      {section.quotes.join(' ')}
                    </blockquote>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Key Points */}
        {result.keyPoints && result.keyPoints.length > 0 && (
          <section className="mb-0">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
              <span className="text-xl">🎯</span>
              {t('contentSummarize.result.keyPoints')}
            </h3>
            <ol className="m-0 pl-6 list-decimal">
              {result.keyPoints.map((point, index) => (
                <li key={index} className="text-base text-gray-900 leading-relaxed mb-2 last:mb-0">{point}</li>
              ))}
            </ol>
          </section>
        )}
      </>
    );
  };

  return (
    <div className="max-w-[900px] mx-auto py-8 px-4">
      <header className="text-center mb-12">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('contentSummarize.title')}</h1>
        <p className="text-base text-gray-500 m-0">{t('contentSummarize.subtitle')}</p>
      </header>

      <div className="flex flex-col gap-8">
        {/* Input Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          {/* API Key Selection */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <label className="flex items-center gap-1 text-base font-medium text-gray-900 mb-2">
              <span className="text-xl">🤖</span>
              AI 모델 선택
            </label>
            {isLoadingKeys ? (
              <div className="flex items-center gap-2 p-4 text-gray-500 text-base">
                <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                <span>모델 목록 불러오는 중...</span>
              </div>
            ) : apiKeyError ? (
              <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <span className="block text-[32px] mb-2">⚠️</span>
                <p className="my-1 text-amber-700">{apiKeyError}</p>
                {apiKeyError.includes('로그인') && (
                  <p className="text-xs text-amber-700 opacity-80">
                    <a href="/login" className="text-blue-500 underline">로그인 페이지로 이동</a>
                  </p>
                )}
                {apiKeyError.includes('인증이 만료') && (
                  <p className="text-xs text-amber-700 opacity-80">
                    <a href="/login" className="text-blue-500 underline">다시 로그인하기</a>
                  </p>
                )}
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <span className="block text-[32px] mb-2">⚠️</span>
                <p className="my-1 text-amber-700">등록된 AI API 키가 없습니다.</p>
                <p className="text-xs text-amber-700 opacity-80">관리자 대시보드에서 API 키를 추가해주세요.</p>
              </div>
            ) : (
              <select
                className="w-full p-4 border border-gray-300 rounded-lg text-base text-gray-900 bg-white cursor-pointer transition-colors hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                value={selectedApiKeyId || ''}
                onChange={(e) => setSelectedApiKeyId(parseInt(e.target.value))}
                disabled={loading}
              >
                {apiKeys.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.providerInfo.name} - {key.label} ({key.model})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              type="button"
              className={`flex-1 py-4 px-6 bg-transparent border-none border-b-2 text-base font-medium cursor-pointer transition-all ${activeTab === 'text' ? 'text-blue-500 border-b-blue-500' : 'text-gray-500 border-b-transparent hover:text-gray-900'}`}
              onClick={() => setActiveTab('text')}
            >
              {t('contentSummarize.tabs.text')}
            </button>
            <button
              type="button"
              className={`flex-1 py-4 px-6 bg-transparent border-none border-b-2 text-base font-medium cursor-pointer transition-all ${activeTab === 'url' ? 'text-blue-500 border-b-blue-500' : 'text-gray-500 border-b-transparent hover:text-gray-900'}`}
              onClick={() => setActiveTab('url')}
            >
              {t('contentSummarize.tabs.url')}
            </button>
            <button
              type="button"
              className={`flex-1 py-4 px-6 bg-transparent border-none border-b-2 text-base font-medium cursor-pointer transition-all ${activeTab === 'file' ? 'text-blue-500 border-b-blue-500' : 'text-gray-500 border-b-transparent hover:text-gray-900'}`}
              onClick={() => setActiveTab('file')}
            >
              {t('contentSummarize.tabs.file')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="mb-6">
            {/* Text Input */}
            {activeTab === 'text' && (
              <div className="w-full">
                <textarea
                  className="w-full p-4 border border-gray-300 rounded-lg text-base text-gray-900 leading-relaxed resize-y transition-colors focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  placeholder={t('contentSummarize.input.textPlaceholder')}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  disabled={loading}
                  rows={10}
                />
              </div>
            )}

            {/* URL Input */}
            {activeTab === 'url' && (
              <div className="w-full">
                <input
                  type="url"
                  className="w-full h-11 px-4 border border-gray-300 rounded-lg text-base text-gray-900 transition-colors focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  placeholder={t('contentSummarize.input.urlPlaceholder')}
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            {/* File Input */}
            {activeTab === 'file' && (
              <div className="w-full">
                <div
                  className={`min-h-[200px] border-2 border-dashed rounded-xl bg-gray-50 flex items-center justify-center cursor-pointer transition-all p-6 ${dragActive ? 'border-blue-500 bg-blue-500/5' : file ? 'border-solid bg-gray-50' : 'border-gray-300 hover:border-blue-500 hover:bg-gray-100'}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    accept=".pdf,.docx,.txt"
                    onChange={handleFileChange}
                    disabled={loading}
                  />
                  {file ? (
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-5xl">📄</span>
                      <span className="text-base font-medium text-gray-900">{file.name}</span>
                      <span className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <span className="text-5xl block mb-4">📁</span>
                      <p className="text-base text-gray-900 mb-1">{t('contentSummarize.input.fileDrop')}</p>
                      <p className="text-xs text-gray-400 m-0">PDF, DOCX, TXT (최대 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-base mb-4">
              <span className="text-xl">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="p-6 bg-gray-100 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-4 mb-4">
                <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                <div className="flex-1">
                  <p className="text-base font-medium text-gray-900 m-0 mb-1">{t('contentSummarize.loading')}</p>
                  <p className="text-xs text-gray-500 m-0">AI가 콘텐츠를 분석하고 있습니다...</p>
                </div>
              </div>
              <div className="w-full h-1 bg-gray-200 rounded overflow-hidden">
                <div className="w-full h-full bg-gradient-to-r from-blue-500 to-blue-400 animate-loading-progress"></div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="h-11 px-6 bg-transparent text-gray-500 border border-gray-300 rounded-lg text-base font-medium cursor-pointer transition-all hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClear}
              disabled={loading}
            >
              {t('contentSummarize.button.clear')}
            </button>
            <button
              type="submit"
              className="h-11 px-6 min-w-[120px] bg-blue-500 text-white border-none rounded-lg text-base font-medium cursor-pointer transition-all hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || apiKeys.length === 0 || !selectedApiKeyId}
            >
              {loading ? t('contentSummarize.loading') : t('contentSummarize.button.summarize')}
            </button>
          </div>
        </form>

        {/* Results */}
        {result && (
          <div className="results-container bg-white border border-gray-200 rounded-xl p-6 shadow-sm animate-fade-in-up">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 m-0">{t('contentSummarize.result.summary')}</h2>
              {result.provider && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs font-medium text-gray-500">
                    {result.provider === 'gemini' && '🔷 Gemini'}
                    {result.provider === 'groq' && '⚡ Groq'}
                    {result.provider === 'anthropic' && '🧠 Claude'}
                    {result.provider === 'openai' && '🤖 GPT'}
                  </span>
                  {result.remaining !== undefined && result.remaining >= 0 && (
                    <span className="inline-flex items-center px-2 py-1 bg-green-100 border border-green-300 rounded text-xs font-medium text-green-700">
                      남은 횟수: {result.remaining}/{result.limit}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Render results */}
            {renderResult()}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <section className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 m-0">{t('contentSummarize.history.title')}</h2>
            </div>

            {isLoadingHistory ? (
              <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                <span>불러오는 중...</span>
              </div>
            ) : (
              <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full border-collapse text-base">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[60px] text-center">번호</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[80px] text-center">유형</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm">요약 내용</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[120px] text-center">날짜</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[80px] text-center">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-200 cursor-pointer transition-all hover:bg-gray-100 ${selectedItem?.id === item.id ? 'bg-blue-500/[0.08] border-l-[3px] border-l-blue-500' : ''}`}
                        onClick={() => handleHistoryItemClick(item)}
                      >
                        <td className="p-4 text-center text-gray-500 font-medium">{history.length - index}</td>
                        <td className="p-4 text-center">
                          <span className="inline-block px-2 py-1 bg-gray-100 border border-gray-200 text-gray-500 text-xs font-medium rounded uppercase">{item.inputType || item.type}</span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1 items-start">
                            {item.keywords && item.keywords.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-1">
                                {item.keywords.slice(0, 3).map((keyword, idx) => (
                                  <span key={idx} className="inline-block px-1 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-[11px] text-blue-500 font-medium">{keyword}</span>
                                ))}
                              </div>
                            )}
                            {item.summary && (
                              <span className="text-sm text-gray-500 leading-snug block">
                                {item.summary.slice(0, 80)}
                                {item.summary.length > 80 ? '...' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center text-gray-500 text-sm">{formatDate(item.createdAt)}</td>
                        <td className="p-4 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded cursor-pointer text-sm text-gray-500 transition-all relative hover:bg-red-500/10 hover:border-red-500"
                              onClick={(e) => handleDeleteItem(e, item.id)}
                              title="삭제"
                            >🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Empty State */}
        {!loading && !result && history.length === 0 && (
          <div className="text-center py-16 px-6">
            <span className="text-6xl block mb-4">📭</span>
            <p className="text-base font-semibold text-gray-900 mb-1">{t('contentSummarize.history.empty')}</p>
            <p className="text-sm text-gray-500 m-0">텍스트, URL 또는 파일을 입력하여 AI 요약을 생성해보세요.</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default ContentSummarize;
